import { gql } from '@apollo/client';

export const GET_USER = gql`
  query GetUser {
    user {
      id
      email
      firstName
      lastName
      gender
      birthDate
      timeZone
      phoneNumber
      profilePicUrl
      type
      pushNotificationsEnabled
      isTestUser
      address {
        city
        state
        streetAddress
        zipCode
        locationType
        accessDetails
      }
      stylist {
        id
        bio
        landscapeImage
        portraitImages
        expertise
        aesthetics
        tiktok
        instagram
        substack
        stripeAccountId
        hasCompletedStripeSetup
        customHourlyRate
        customInPersonHourlyRate
        isAvailableInPerson
        computedInPersonAvailability
        pricing {
          id
          hourlyRate
        }
        instantStyling {
          id
          isActive
          updatedAt
        }
      }
      client {
        id
        aesthetics
        bio
        sizingDetails
        height
        shareAge
        stripeCustomerId
        stripeDefaultPaymentMethodId
      }
    }
  }
`;

export const GET_USER_NOTIFICATION_SETTINGS = gql`
  query GetUserNotificationSettings {
    getUserNotificationSettings {
      id
      emailNotificationsEnabled
      pushNotificationsEnabled
    }
  }
`;

export const ACTIVE_VIDEO_SESSION = gql`
  query ActiveVideoSession {
    activeVideoSession {
      id
      sessionName
      hostId
      guestId
      isActive
      startedAt
      endedAt
    }
  }
`;

export const JOIN_VIDEO_SESSION = gql`
  query JoinVideoSession($input: JoinVideoSessionInput!) {
    joinVideoSession(input: $input) {
      session {
        id
        sessionName
        hostId
        guestId
        isActive
        startedAt
        endedAt
      }
      zoomToken
      twilioToken
    }
  }
`;

export const GET_DEFAULT_PAYMENT_METHOD = gql`
  query GetDefaultPaymentMethod {
    getDefaultPaymentMethod {
      paymentMethodId
      last4
      brand
    }
  }
`;

export const GET_SETUP_INTENT = gql`
  query GetSetupIntent {
    getSetupIntent {
      clientSecret
      ephemeralKey
    }
  }
`;

export const GET_STRIPE_ACCOUNT_LINK = gql`
  query GetStripeAccountLink {
    stripeAccountLink
  }
`;

export const GET_STRIPE_ACCOUNT_IS_ACTIVE = gql`
  query GetStripeAccountIsActive {
    stripeAccountIsActive
  }
`;

export const GET_CONSULTATION = gql`
  query GetConsultation($input: ConsultationInput!) {
    consultation(input: $input) {
      id
      clientId
      stylistId
      isInstant
      startTime
      cancelTime
      estimatedDuration
      endTime
      estimatedPrice
      overageRate
      status
      consultationDetails {
        reason
        style
        clientNotes
      }
      videoSession {
        id
        sessionName
        isActive
      }
    }
  }
`;

export const GET_CONSULTATIONS = gql`
  query GetConsultations {
    consultations {
      id
      status
      isInstant
      startTime
      endTime
      cancelTime
      clientId
      stylistId
      estimatedDuration
      sessionType
      estimatedPrice
      stylist {
        id
        customHourlyRate
        customInPersonHourlyRate
        pricing {
          id
          hourlyRate
          tierName
        }
        user {
          id
          firstName
          lastName
          gender
          birthDate
          profilePicUrl
        }
      }
      client {
        id
        aesthetics
        bio
        sizingDetails
        height
        shareAge
        user {
          id
          firstName
          lastName
          profilePicUrl
          address {
            streetAddress
            city
            state
            zipCode
            locationType
            accessDetails
          }
        }
      }
      consultationDetails {
        reason
        clientNotes
      }
      videoSession {
        id
        sessionName
        isActive
      }
      payment {
        id
        discountAmount
        discountCode {
          id
          codeText
          discountPercentage
        }
      }
    }
  }
`;

export const GET_AVAILABILITY_SLOTS = gql`
  query GetAvailabilitySlots($input: AvailabilitySlotsInput!) {
    availabilitySlots(input: $input) {
      startTime
      endTime
    }
  }
`;

export const GET_NOTIFICATIONS = gql`
  query GetNotifications($input: NotificationListInput!) {
    notifications(input: $input) {
      notifications {
        id
        userId
        type
        isRead
        deepLinkUrl
        targetType
        targetId
        variables
        createdAt
      }
      total
      hasMore
    }
  }
`;

export const GET_UNREAD_NOTIFICATIONS_COUNT = gql`
  query GetUnreadNotificationsCount {
    unreadNotificationsCount
  }
`;

export const GET_CLIENT = gql`
  query GetClient($input: GetClientInput!) {
    client(input: $input) {
      id
      aesthetics
      bio
      sizingDetails
      height
      shareAge
      user {
        id
        firstName
        lastName
        profilePicUrl
        birthDate
        address {
          city
          state
        }
      }
    }
  }
`;

export const GET_PRICING = gql`
  query GetPricing {
    pricing {
      id
      tierName
      hourlyRate
    }
  }
`;

export const GET_INSTANT_STYLING_REQUEST = gql`
  query instantStylingRequest($input: InstantStylingRequestInput!) {
    instantStylingRequest(input: $input) {
      id
      clientId
      stylistId
      pricingTier
      duration
      reason
      gender
      age
      style
      details
      expiresAt
      client {
        id
        user {
          id
        }
      }
      stylist {
        id
        user {
          id
        }
      }
    }
  }
`;

export const GET_LOOKBOOK = gql`
  query GetLookbook($input: LookbookQueryInput!) {
    lookbook(input: $input) {
      id
      sendbirdMessageId
      stylistId
      clientId
      price
      note
      status
      paymentId
      stylist {
        id
        user {
          id
          firstName
          lastName
        }
      }
      client {
        id
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const CHECK_SIGNNOW_DOCUMENT_STATUS = gql`
  query CheckSignNowDocumentStatus($documentId: String!) {
    checkSignNowDocumentStatus(documentId: $documentId)
  }
`;

export const GET_CLIENT_NOTES = gql`
  query GetClientNotes($input: ClientNotesInput!) {
    clientNotes(input: $input) {
      id
      clientId
      stylistId
      content
      createdAt
      updatedAt
    }
  }
`;

export const CHECK_CLIENT_HAS_CONSULTATION = gql`
  query CheckClientHasConsultation($input: CheckClientHasConsultationInput!) {
    checkClientHasConsultation(input: $input) {
      hasConsultation
    }
  }
`;

export const VALIDATE_DISCOUNT_CODE = gql`
  query ValidateDiscountCode($input: ValidateDiscountCodeInput!) {
    validateDiscountCode(input: $input) {
      valid
      discountPercentage
      message
    }
  }
`;
